import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Loader2, MessageSquare, User, Bot, Clock, Globe } from 'lucide-react';
import { format } from 'date-fns';
import { API_URL } from '../config/api';

interface ChatEvent {
  timestamp: string;
  type: string;
  pageUrl?: string;
  pageTitle?: string;
  data?: {
    message?: string;
    reply?: string;
    confidence?: number;
  };
}

interface ChatSessionReplayProps {
  sessionId: string;
  onClose?: () => void;
}

export default function ChatSessionReplay({ sessionId, onClose }: ChatSessionReplayProps) {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<ChatEvent[]>([]);
  const [sessionDetails, setSessionDetails] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSessionDetails();
  }, [sessionId]);

  const fetchSessionDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/api/sessions/replays/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch session details');
      }
      
      const data = await response.json();
      setSessionDetails(data);
      setEvents(data.events || []);
    } catch (error) {
      console.error('Error fetching session details:', error);
      setError('Failed to load session details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">{error}</p>
        <Button onClick={fetchSessionDetails} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Session Info Header */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Session Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Session ID:</span>
              <p className="font-mono">{sessionId}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Duration:</span>
              <p>{sessionDetails?.duration || 0} seconds</p>
            </div>
            <div>
              <span className="text-muted-foreground">Start Time:</span>
              <p>{sessionDetails?.startTime ? format(new Date(sessionDetails.startTime), 'MMM d, yyyy HH:mm:ss') : 'N/A'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Total Messages:</span>
              <p>{events.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Chat Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Conversation Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {events.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No conversation events found for this session
              </p>
            ) : (
              events.map((event, index) => (
                <div key={index} className="border rounded-lg p-4">
                  {/* Event Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(event.timestamp), 'HH:mm:ss')}
                      </span>
                      {event.pageUrl && (
                        <>
                          <span className="text-muted-foreground">•</span>
                          <Globe className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            {event.pageTitle || event.pageUrl}
                          </span>
                        </>
                      )}
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {event.type}
                    </Badge>
                  </div>

                  {/* Chat Messages */}
                  {event.data && (
                    <div className="space-y-3">
                      {/* User Message */}
                      {event.data.message && (
                        <div className="flex gap-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <User className="h-4 w-4 text-blue-600" />
                            </div>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium mb-1">User</p>
                            <div className="bg-blue-50 rounded-lg p-3">
                              <p className="text-sm">{event.data.message}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Bot Reply */}
                      {event.data.reply && (
                        <div className="flex gap-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                              <Bot className="h-4 w-4 text-green-600" />
                            </div>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium mb-1">
                              PageMind
                              {event.data.confidence && (
                                <span className="text-xs text-muted-foreground ml-2">
                                  (Confidence: {(event.data.confidence * 100).toFixed(0)}%)
                                </span>
                              )}
                            </p>
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-sm whitespace-pre-wrap">{event.data.reply}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}